import './_connection-map.scss';
import { EUROPEAN_COUNTRIES } from "@/fragments/connection-map/countries/countries";
import * as d3 from 'd3';
import { resolve } from "@/index";
import { SPINNER_DURATION } from "@/ts/enums";

export default function ConnectionMap() {
    const $connectionMap = document.querySelectorAll('.connection-map');

    if ($connectionMap.length) {
        $connectionMap.forEach(($item: Element) => {
            let outData: any;
            let geoJson: object;
            let currentTab = null as any;
            const $noResult = $item.querySelector('.no-result') as HTMLDivElement;
            const $tabs = $item.querySelectorAll('.tab');
            const $contentTabs = $item.querySelector('.tab-contents') as HTMLDivElement;
            const $tabsContent = $item.querySelectorAll('.tab-content');
            const $connectionsList = $contentTabs.querySelector('.connections') as HTMLDivElement;
            const $zoomInBtn = $item.querySelectorAll('.zoom-in-btn');
            const $zoomOutBtn = $item.querySelectorAll('.zoom-out-btn');
            const $legend = $item.querySelector('.data-legend-list') as HTMLDivElement;
            const $modal = $item.querySelector('.map-modal') as HTMLDialogElement;
            const $fullScreenBtn = $item.querySelector('.full-screen-btn') as HTMLElement;
            const $closeModalBtn = $modal.querySelector('.close-btn') as HTMLElement;
            const $tabMap = $item.querySelector('.tab-content[data-id="map"]') as HTMLDivElement;
            const $listContent = $item.querySelector('.tab-content[data-id="list"]') as HTMLDivElement;
            const $smallDevices = $item.querySelector('.small-devices') as HTMLDivElement;
            const $mapContainer = $item.querySelectorAll('.map-container');
            let commonConnections: any = [];

            let projection = d3.geoAzimuthalEqualArea();
            let geoGenerator = d3.geoPath().projection(projection);
            let svg = d3.selectAll('svg');
            let g = d3.selectAll('g');
            svg.attr('viewBox', '0 0 720 595').attr('preserveAspectRatio', 'xMidYMid');

            let zoom = d3.zoom().scaleExtent([0.5, 3]).on('zoom', handleZoom);
            let zoomModal = d3.zoom().scaleExtent([0.5, 3]).on('zoom', handleZoom);

            d3.select('svg').call(zoom);
            d3.select('.modal svg').call(zoomModal);

            function getCommonConnections(connectionsFromItaly: any, connectionsToItaly: any) {
                if(connectionsFromItaly.connections && connectionsFromItaly.connections.length && connectionsToItaly.connections && connectionsToItaly.connections.length) {
                    commonConnections = connectionsFromItaly.connections.filter((connection: any) => connectionsToItaly.connections.includes(connection));
                }
            }

            function ui() {
                try {
                    const $geoMap = $item.querySelector('[data-map]') as HTMLScriptElement;
                    geoJson = $geoMap ? JSON.parse($geoMap.innerText) : null;

                    if (geoJson) {
                        bindMap();
                    } else {
                        return null;
                    }
                } catch (e) {
                    $item.classList.add('d-none');
                    console.log('unable to retrieve map json');
                }

                $zoomInBtn.forEach(($btn: any) => {
                    $btn.addEventListener('click', () => zoomIn());
                    $btn.addEventListener('keyup', (e: KeyboardEvent) => {
                        if (e.key === 'Enter') {
                            zoomIn();
                        }
                    });
                });

                $zoomOutBtn.forEach(($btn: any) => {
                    $btn.addEventListener('click', () => zoomOut());
                    $btn.addEventListener('keyup', (e: KeyboardEvent) => {
                        if (e.key === 'Enter') {
                            zoomOut();
                        }
                    });
                });

                $fullScreenBtn.addEventListener('click', () => openModal());
                $fullScreenBtn.addEventListener('keypress', (e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                        openModal();
                    }
                })

                $closeModalBtn.addEventListener('click', () => closeModal());
                $closeModalBtn.addEventListener('keypress', (e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                        closeModal();
                    }
                })

                document.addEventListener('click', (event: MouseEvent) => {
                    if ($modal && !(event.target as HTMLElement).contains($modal)) return;
                    closeModal();
                });

                if($mapContainer.length) {
                    $mapContainer.forEach(($map: Element) => {
                        $map.addEventListener('keydown', function (e: any) {
                            if (e.key === 'Escape') {
                                $tabMap.classList.remove('modal-open');
                            }

                            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();

                                if (e.key === 'ArrowLeft') {
                                    panLeft();
                                }

                                if (e.key === 'ArrowRight') {
                                    panRight();
                                }

                                if (e.key === 'ArrowUp') {
                                    panUp();
                                }

                                if (e.key === 'ArrowDown') {
                                    panDown();
                                }
                            }
                        });
                    })
                }

                window.addEventListener('orientationchange', () => {
                    if (window.innerHeight <= 649 && window.innerHeight < window.innerWidth) {
                        closeModal();
                    }
                });

                return;
            }

            function handleTabs() {
                $tabs.forEach(($tab: any, index: number) => {
                    if (index === 0) {
                        $tab.classList.add('selected');
                        $tab.setAttribute('aria-selected', true);
                        $tab.setAttribute('aria-hidden', false);
                        $tab.setAttribute('tabindex', '0');

                        $tabsContent.forEach(($content: any) => {
                            const id = $content.getAttribute('data-id') as string;

                            if ($tab.getAttribute('data-id') === id) {
                                $content.classList.add('selected');
                                $content.setAttribute('aria-hidden', false);
                                $contentTabs.classList.add(id);
                                if(id ==='map') {
                                    $connectionsList && $connectionsList.setAttribute('aria-hidden', "true");
                                }
                            } else {
                                $content.classList.remove('selected');
                                $contentTabs.classList.remove(id);
                            }
                        });
                    }

                    $tab.addEventListener('click', () => {
                        $tabs.forEach(($el: any) => {
                            $el.classList.remove('selected');
                            $el.setAttribute('aria-selected', false);
                            $el.setAttribute('tabindex', '-1');
                        });
                        $tab.classList.add('selected');
                        $tab.setAttribute('aria-selected', true);
                        $tab.setAttribute('tabindex', '0');

                        $tabsContent.forEach(($content: any) => {
                            const id = $content.getAttribute('data-id') as string;
                            if ($tab.getAttribute('data-id') === id) {
                                $content.classList.add('selected');
                                $contentTabs.classList.add(id);
                                if(id !== "map") {
                                    $connectionsList && $connectionsList.removeAttribute('aria-hidden');
                                } else {
                                    $connectionsList && $connectionsList.setAttribute('aria-hidden', "true");
                                }
                            } else {
                                $content.classList.remove('selected');
                                $contentTabs.classList.remove(id);
                            }
                        });
                    });

                    $tab.addEventListener('keydown', function (event: any) {
                        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                            if (event.key === 'ArrowLeft') {
                                currentTab = currentTab === 0 ? $tabs.length - 1 : currentTab - 1;
                            } else {
                                currentTab = currentTab === $tabs.length - 1 ? 0 : currentTab + 1;
                            }

                            ($tabs[currentTab] as any).focus();
                        }
                    });

                    if (!$tab.classList.contains('selected')) {
                        $tab.setAttribute('tabindex', '-1');
                    }
                });
            }

            function getCoordinates(countryCode: string) {
                const country = Object.values(EUROPEAN_COUNTRIES).find(el => el.label === countryCode);
                if (country) {
                    return [country.long, country.lat];
                } else {
                    return null;
                }
            }

            function initMap(geoJson: any, connectionsFromItaly: any) {
                projection.fitExtent(
                    [
                        [0, 0],
                        [720, 595],
                    ],
                    geoJson
                );

                let u = d3.selectAll('.map-container g.map').selectAll('path').data(geoJson.features);

                u.enter()
                    .append('path')
                    .attr('d', geoGenerator)


                const italyCoordinates = getCoordinates(connectionsFromItaly.code);
                if (italyCoordinates && commonConnections.length) {
                    const lines = g.append("g").attr("class", "lines");
                    const circles = g.append("g").attr("class", "circles");
                    const texts = g.append("g").attr("class", "texts");

                    commonConnections.forEach((connection: any) => {
                        const countryCoordinates = getCoordinates(connection);
                        if (countryCoordinates) {
                            const legendType3 = $legend.querySelector('li[data-id="type-3"]') as HTMLElement;

                            lines.append("line")
                                .attr("x1", projection(italyCoordinates)[0])
                                .attr("y1", projection(italyCoordinates)[1])
                                .attr("x2", projection(countryCoordinates)[0])
                                .attr("y2", projection(countryCoordinates)[1])
                                .attr("stroke", legendType3.getAttribute('data-color'))
                                .attr("stroke-dasharray", '3,3')
                                .attr("stroke-width", 1.5);

                            circles.append("circle")
                                .attr("cx", projection(countryCoordinates)[0])
                                .attr("cy", projection(countryCoordinates)[1])
                                .attr("r", 12)
                                .attr("class", "connection");

                            circles.append("circle")
                                .attr("cx", projection(italyCoordinates)[0])
                                .attr("cy", projection(italyCoordinates)[1])
                                .attr("r", 12)
                                .attr("class", "italy");

                            texts.append("text")
                                .attr("x", projection(countryCoordinates)[0])
                                .attr("y", projection(countryCoordinates)[1] + 4.5)
                                .attr("class", "connection")
                                .text(connection);

                            texts.append("text")
                                .attr("x", projection(italyCoordinates)[0])
                                .attr("y", projection(italyCoordinates)[1] + 4.5)
                                .attr("class", "italy")
                                .text(connectionsFromItaly.code);
                        }
                    });
                }
            }

            function openModal() {
                $modal.showModal();
                $tabMap.classList.add('modal-open');

                initMap(geoJson, outData);
            }

            function closeModal() {
                $modal.close();
                $tabMap.classList.remove('modal-open');
            }

            function handleZoom(event: any) {
                if ($modal && $tabMap.classList.contains('modal-open')) {
                    d3.select('.modal svg g').attr('transform', event.transform);
                } else {
                    d3.select('svg g').attr('transform', event.transform);
                }
            }

            function zoomIn() {
                if ($modal && $tabMap.classList.contains('modal-open')) {
                    d3.select('.modal svg').transition().call(zoomModal.scaleBy, 1.5);
                } else {
                    d3.select('svg').transition().call(zoom.scaleBy, 1.5);
                }
            }

            function zoomOut() {
                if ($modal && $tabMap.classList.contains('modal-open')) {
                    d3.select('.modal svg').transition().call(zoomModal.scaleBy, 0.75);
                } else {
                    d3.select('svg').transition().call(zoom.scaleBy, 0.75);
                }
            }

            function panLeft() {
                d3.select('svg').transition().call(zoom.translateBy, -50, 0);
            }

            function panRight() {
                d3.select('svg').transition().call(zoom.translateBy, 50, 0);
            }

            function panUp() {
                d3.select('svg').transition().call(zoom.translateBy, 0, -50);
            }

            function panDown() {
                d3.select('svg').transition().call(zoom.translateBy, 0, 50);
            }


            function createCountry(countryItem: any, connection: string) {
                const country = Object.keys(EUROPEAN_COUNTRIES).find((key: string) => (EUROPEAN_COUNTRIES as any)[key].label === connection);
                if (country) {
                    countryItem.textContent = Drupal.t(country) + ` (${connection})`;
                }
            }

            function initList() {
                if (commonConnections.length) {
                    commonConnections.forEach((connection: any) => {
                        const country = Object.values(EUROPEAN_COUNTRIES).find(el => el.label === connection) as any;
                        const flag = document.createElement('img');
                        if (country && country.label != 'IT') {
                            const countryElement = document.createElement('li');
                            countryElement.classList.add('country-item');
                            const countryItem = document.createElement('div');
                            countryItem.classList.add('country');
                            flag.setAttribute('src', country.flag)
                            flag.setAttribute('alt', " ");
                            countryElement.appendChild(flag)
                            createCountry(countryItem, connection);

                            const $biConnections = $item.querySelector('.countries[id="type-3"]') as HTMLDivElement;
                            const list = $biConnections.querySelector('.connections') as HTMLDivElement;
                            countryElement.appendChild(countryItem);
                            list.appendChild(countryElement);
                        }
                    })
                }
            }

            function handleApiError() {
                setTimeout(() => {
                    $noResult && $noResult.classList.remove('d-none');
                    $item.classList.remove('has-spinner');
                    $tabMap.classList.add('d-none');
                    $listContent.classList.add('d-none');
                    $smallDevices.classList.add('d-none');
                }, SPINNER_DURATION);
            }

            function bindMap() {
                $item.classList.add('has-spinner');

                try {
                    resolve
                        .run("getConnections")
                        .then((res: any) => {
                            if (!res) {
                                throw new Error();
                            } else {
                                outData = res[0].data[0];
                                const inData = res[1].data[0];
                                setTimeout(() => {
                                    getCommonConnections(outData, inData);
                                    initMap(geoJson, outData);
                                    initList();
                                    $noResult && $noResult.classList.add('d-none');
                                    $item.classList.remove('has-spinner');
                                }, SPINNER_DURATION);
                            }
                        })
                        .catch(e => {
                            //console.log(e);
                            handleApiError();
                        });
                } catch (e) {
                    console.log(e);
                    $item.classList.remove('has-spinner');
                }
            }

            function init() {
                handleTabs();
                ui();
            }

            init();
        })
    }
}
